import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Call, Device } from '@twilio/voice-sdk';
import { getPhoneCallBySid, getPhoneStatus, getPhoneToken, setPhonePresence } from '@/data/client';
import type { PhoneCall, PhoneStatus } from '@/data/types';

/* The softphone's state for the whole app: one Twilio Device per tab, created
   only when browser calling is configured, registered for inbound calls only
   while the member is "available". Everything the UI needs (ring, answer, dial,
   mute, hang up, and the call that just ended for the outcome prompt) comes
   from here; the top bar and the Call buttons are views of it.

   The SDK is loaded on demand so the bundle does not carry it for workspaces
   without a phone. */

export type PhoneUiState = 'off' | 'idle' | 'ringing' | 'connecting' | 'in_call';

interface PhoneContextValue {
  status: PhoneStatus | undefined;
  enabled: boolean;              // browser calling configured
  available: boolean;
  setAvailable: (v: boolean) => void;
  state: PhoneUiState;
  muted: boolean;
  error: string | null;
  /** The ringing or connected call's account details, once known. */
  current: PhoneCall | null;
  currentLabel: string;
  startedAt: number | null;
  dial: (to: string, label?: string) => Promise<void>;
  answer: () => void;
  reject: () => void;
  hangUp: () => void;
  toggleMute: () => void;
  /** The call that just ended, for the outcome prompt; dismiss clears it. */
  ended: PhoneCall | null;
  dismissEnded: () => void;
}

const PhoneContext = createContext<PhoneContextValue | null>(null);

export function usePhone(): PhoneContextValue {
  const v = useContext(PhoneContext);
  if (!v) throw new Error('usePhone outside PhoneProvider');
  return v;
}

export function PhoneProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ['phoneStatus'], queryFn: getPhoneStatus, staleTime: 5 * 60_000, retry: false });
  const enabled = !!status?.browserCalling;
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const [state, setState] = useState<PhoneUiState>('off');
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [current, setCurrent] = useState<PhoneCall | null>(null);
  const [currentLabel, setCurrentLabel] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [ended, setEnded] = useState<PhoneCall | null>(null);
  const [available, setAvailableState] = useState(false);
  useEffect(() => { if (status) setAvailableState(status.available); }, [status]);

  /* Look the call up by its Twilio SID: the webhook has already matched the
     number to an account by the time the browser rings or connects. */
  const describe = useCallback(async (sid: string | undefined, fallback: string) => {
    if (!sid) { setCurrentLabel(fallback); return; }
    try {
      const c = await getPhoneCallBySid(sid);
      setCurrent(c);
      setCurrentLabel(c.who ? `${c.who}${c.companyName && c.companyName !== c.who ? ` · ${c.companyName}` : ''}` : c.otherParty || fallback);
      return c;
    } catch { setCurrentLabel(fallback); }
  }, []);

  const wire = useCallback((call: Call, fallback: string) => {
    callRef.current = call;
    setMuted(false);
    call.on('accept', () => {
      setState('in_call');
      setStartedAt(Date.now());
      void describe(call.parameters.CallSid, fallback);
    });
    call.on('mute', (m: boolean) => setMuted(m));
    call.on('error', (e: { message?: string }) => setError(e.message ?? 'Call error'));
    const finish = () => {
      const sid = call.parameters.CallSid;
      callRef.current = null;
      setState('idle');
      setStartedAt(null);
      setCurrent(null);
      // The webhooks need a moment to close the row; the prompt shows whatever is known.
      if (sid) {
        window.setTimeout(() => {
          getPhoneCallBySid(sid).then((c) => { setEnded(c); void qc.invalidateQueries({ queryKey: ['phoneCalls'] }); }).catch(() => undefined);
        }, 1200);
      }
    };
    call.on('disconnect', finish);
    call.on('cancel', finish);
    call.on('reject', () => { callRef.current = null; setState('idle'); setCurrent(null); });
  }, [describe, qc]);

  // Build the Device once calling is possible; tear it down with the tab.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const { Device, Call } = await import('@twilio/voice-sdk');
        const { token } = await getPhoneToken();
        if (cancelled) return;
        const device = new Device(token, { codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU], closeProtection: true, logLevel: 'error' });
        device.on('tokenWillExpire', () => { getPhoneToken().then((t) => device.updateToken(t.token)).catch(() => setError('Could not refresh the phone token.')); });
        device.on('error', (e: { message?: string }) => setError(e.message ?? 'Phone error'));
        device.on('incoming', (call: Call) => {
          if (callRef.current) { call.reject(); return; } // already on a call
          wire(call, call.parameters.From ?? 'Unknown caller');
          setState('ringing');
          setCurrentLabel(call.parameters.From ?? 'Unknown caller');
          void describe(call.parameters.CallSid, call.parameters.From ?? 'Unknown caller');
        });
        deviceRef.current = device;
        setState('idle');
      } catch (e) { setError(e instanceof Error ? e.message : 'Could not start the phone.'); }
    })();
    return () => { cancelled = true; deviceRef.current?.destroy(); deviceRef.current = null; setState('off'); };
  }, [enabled, wire, describe]);

  // Ring this tab for inbound calls only while available.
  useEffect(() => {
    const d = deviceRef.current;
    if (!d || state === 'off') return;
    if (available) d.register().catch((e: Error) => setError(e.message));
    else if (d.state === 'registered') d.unregister().catch(() => undefined);
  }, [available, state]);

  const presence = useMutation({ mutationFn: setPhonePresence, onSuccess: () => void qc.invalidateQueries({ queryKey: ['phoneStatus'] }) });
  const setAvailable = useCallback((v: boolean) => { setAvailableState(v); presence.mutate(v); }, [presence]);

  const dial = useCallback(async (to: string, label?: string) => {
    const d = deviceRef.current;
    if (!d) { setError('The phone is not ready.'); return; }
    if (callRef.current) { setError('Already on a call.'); return; }
    setError(null);
    setState('connecting');
    setCurrentLabel(label ?? to);
    try {
      const call = await d.connect({ params: { To: to } });
      wire(call, label ?? to);
    } catch (e) { setState('idle'); setError(e instanceof Error ? e.message : 'Could not place the call.'); }
  }, [wire]);

  const answer = useCallback(() => { callRef.current?.accept(); }, []);
  const reject = useCallback(() => { callRef.current?.reject(); callRef.current = null; setState('idle'); setCurrent(null); }, []);
  const hangUp = useCallback(() => { callRef.current?.disconnect(); }, []);
  const toggleMute = useCallback(() => { const c = callRef.current; if (c) c.mute(!c.isMuted()); }, []);
  const dismissEnded = useCallback(() => setEnded(null), []);

  const value = useMemo<PhoneContextValue>(() => ({
    status, enabled, available, setAvailable, state, muted, error, current, currentLabel, startedAt, dial, answer, reject, hangUp, toggleMute, ended, dismissEnded,
  }), [status, enabled, available, setAvailable, state, muted, error, current, currentLabel, startedAt, dial, answer, reject, hangUp, toggleMute, ended, dismissEnded]);

  return <PhoneContext.Provider value={value}>{children}</PhoneContext.Provider>;
}
